import React from 'react'
import { AlertTriangle, RefreshCw, Mail, MailPlus, ShieldAlert, ClipboardList, Send, Zap, UserCheck, Bell, Ban } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useAutomationSettings } from '../hooks/useAutomationSettings'
import { useAutomationActionStatuses } from '../hooks/useAutomationActionStatuses'
import type { AutomationMode } from '../types'

type Props = {
  workspaceId: string
  role: 'owner' | 'admin' | 'member'
}

// Mirrors apps/api/src/features/automation/types.ts (ALLOWED_MODES) — duplicated
// here rather than shared because the frontend has no build-time dependency on the
// backend package. allowedModes must match the backend exactly (enforced server-side
// too); this list just keeps the UI from offering something the backend would reject.
//
// `category` groups actions by which underlying feature/workflow they belong to —
// not the earlier Internal/Customer-facing split (removed previously for reading as
// jargon without explaining anything a rep needed). This grouping is different: it's
// literally which pipeline each action is a knob on (re-engagement's reply-handling
// chain, the campaigns feature, or HubSpot deal hygiene — see automation-architecture.md
// and campaigns-architecture.md), so a rep can tell at a glance which rows affect the
// same thing. `sendsToLeads` still carries the Auto-execute-cap explanation inline.
type ActionCategory = 'followups' | 'campaigns' | 'deals'

type ActionDefinition = {
  key: string
  label: string
  description: string
  icon: LucideIcon
  category: ActionCategory
  sendsToLeads: boolean
  allowedModes: AutomationMode[]
}

const CATEGORY_META: Record<ActionCategory, { label: string; description: string }> = {
  followups: {
    label: 'Follow-ups & Replies',
    description: 'What Zeph does when a lead goes quiet, or replies raising an objection.',
  },
  campaigns: {
    label: 'Outreach Campaigns',
    description: 'Email sequences with automatic follow-ups — starting one, and sending each scheduled follow-up.',
  },
  deals: {
    label: 'Deal Hygiene',
    description: 'Keeping open HubSpot deals from going stale.',
  },
}

// Render order — leads-facing, most-frequently-touched workflows first; internal
// housekeeping (no lead ever sees it) last.
const CATEGORY_ORDER: ActionCategory[] = ['followups', 'campaigns', 'deals']

const ACTIONS: ActionDefinition[] = [
  {
    key: 'automation.reengagement_email',
    label: 'Re-engagement follow-up email',
    description: "When a lead hasn't replied to a sent email within the configured window, this controls what Zeph does about it.",
    icon: Mail,
    category: 'followups',
    sendsToLeads: true,
    // Auto-execute is intentionally absent — automation that reaches a real lead is
    // capped below Auto-execute until the Sample Review Queue exists to make
    // unsupervised sends safe (enforced server-side too, in automation/service.ts).
    allowedModes: ['approval', 'notify', 'off'],
  },
  {
    key: 'automation.objection_reply',
    label: 'Objection/rejection reply handling',
    description: "When a lead's reply raises an objection or rejection, this controls what Zeph does about it.",
    icon: ShieldAlert,
    category: 'followups',
    sendsToLeads: true,
    // Same cap as reengagement_email, same reason.
    allowedModes: ['approval', 'notify', 'off'],
  },
  {
    key: 'automation.campaign_start',
    label: 'Start an outreach campaign',
    description: 'When someone creates an outreach campaign, this controls whether it needs a go-ahead before it starts sending.',
    icon: MailPlus,
    category: 'campaigns',
    sendsToLeads: true,
    // approval/off only — no auto (never silent) and no notify either: a campaign
    // either gets a human's go-ahead before any lead is touched, or it doesn't
    // start at all. There's no sane "notify only" reading of "start this."
    allowedModes: ['approval', 'off'],
  },
  {
    key: 'automation.campaign_send_touch',
    label: 'Send a scheduled campaign follow-up',
    description: 'Once a campaign is approved, this controls whether each scheduled follow-up sends on its own.',
    icon: Send,
    category: 'campaigns',
    sendsToLeads: true,
    // Not capped by the "no auto" customer-facing rule above — a human already
    // approved the campaign as a whole via campaign_start, so individual follow-ups
    // sending automatically from there matches the Governance Matrix's default.
    allowedModes: ['auto', 'notify', 'off'],
  },
  {
    key: 'automation.stale_deal_followup_task',
    label: 'Follow-up task on a stale deal',
    description: 'When an open deal has had no activity for a while, this controls whether Zeph creates a follow-up task on it.',
    icon: ClipboardList,
    category: 'deals',
    sendsToLeads: false,
    // Ask for approval is intentionally absent — the Approval Queue is currently
    // shaped for email drafts only; generalizing it to non-email payloads is
    // deferred until 2-3 examples exist to design it against (enforced server-side).
    allowedModes: ['auto', 'notify', 'off'],
  },
]

const MODE_META: Record<AutomationMode, { label: string; icon: LucideIcon }> = {
  auto: { label: 'Auto-execute', icon: Zap },
  approval: { label: 'Ask for approval', icon: UserCheck },
  notify: { label: 'Notify only', icon: Bell },
  off: { label: 'Off', icon: Ban },
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'No activity yet'
  const diffMs = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// Same segmented-control look as crm-permissions' DecisionToggle and
// QualificationNotificationSettingsSection's local ModeToggle, kept local per the
// same feature-ownership reasoning as that one. Icons added per mode so the row
// reads at a glance instead of requiring the label text to be parsed every time.
const ModeToggle: React.FC<{
  value: AutomationMode
  modes: AutomationMode[]
  onChange: (value: AutomationMode) => void
  disabled?: boolean
}> = ({ value, modes, onChange, disabled }) => {
  const { currentTheme: t } = useTheme()

  return (
    <div
      className="inline-flex flex-wrap items-center gap-1 rounded-lg p-1"
      style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
    >
      {modes.map((mode) => {
        const isActive = value === mode
        const { label, icon: Icon } = MODE_META[mode]
        return (
          <button
            key={mode}
            type="button"
            disabled={disabled}
            onClick={() => onChange(mode)}
            className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11.5px] font-medium transition-colors whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              backgroundColor: isActive ? (mode === 'off' ? t.errorBackground : t.accent) : 'transparent',
              color: isActive ? (mode === 'off' ? t.errorText : t.buttonText) : t.secondrytext,
            }}
          >
            <Icon size={12} />
            {label}
          </button>
        )
      })}
    </div>
  )
}

const AutomationSettingsSection: React.FC<Props> = ({ workspaceId, role }) => {
  const { currentTheme: t } = useTheme()
  const canManage = role === 'owner' || role === 'admin'
  const { settings, loading, error, retry, saving, saveError, setMode } = useAutomationSettings(workspaceId)
  const { statuses } = useAutomationActionStatuses(workspaceId)

  return (
    <div className="flex flex-col gap-6">
      {loading && (
        <div className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
      )}

      {!loading && (error || !settings) && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-xl"
            style={{ backgroundColor: t.errorBackground }}
          >
            <AlertTriangle size={24} style={{ color: t.errorText }} />
          </div>
          <div>
            <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load automation settings</p>
            <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error || 'Something went wrong.'}</p>
          </div>
          <button
            type="button"
            onClick={retry}
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && settings && (
        <>
          <div className="flex flex-col gap-3">
            {!canManage && (
              <p className="text-[12.5px]" style={{ color: t.mutedText }}>
                Only the workspace owner or an admin can change these — you can still see the current setting.
              </p>
            )}

            <div className="flex flex-col gap-5">
              {CATEGORY_ORDER.map((category) => {
                const categoryActions = ACTIONS.filter((a) => a.category === category)
                if (categoryActions.length === 0) return null
                const { label: categoryLabel, description: categoryDescription } = CATEGORY_META[category]

                return (
                  <div key={category} className="flex flex-col gap-2.5">
                    <div>
                      <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: t.mutedText }}>
                        {categoryLabel}
                      </span>
                      <p className="text-[12px] mt-0.5" style={{ color: t.mutedText }}>{categoryDescription}</p>
                    </div>

                    <div className="flex flex-col gap-3">
                      {categoryActions.map((action) => {
                        const status = statuses[action.key]
                        const Icon = action.icon
                        return (
                          <div
                            key={action.key}
                            className="rounded-xl p-4 flex flex-col gap-3"
                            style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
                          >
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                              <div className="flex items-start gap-3 min-w-0">
                                <div
                                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                                  style={{ backgroundColor: t.hoverBackground }}
                                >
                                  <Icon size={15} style={{ color: t.mutedText }} />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <label className="text-sm font-semibold" style={{ color: t.text }}>
                                      {action.label}
                                    </label>
                                    {action.sendsToLeads && (
                                      <span
                                        className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                                        style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}
                                        title="This can send something to a real lead — Auto-execute is unavailable until it's been reviewed at least once."
                                      >
                                        Sends to leads
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[12.5px] mt-0.5" style={{ color: t.mutedText }}>
                                    {action.description}
                                  </p>
                                </div>
                              </div>
                              <ModeToggle
                                value={settings.actions[action.key] ?? action.allowedModes[0]!}
                                modes={action.allowedModes}
                                onChange={(mode) => void setMode(action.key, mode)}
                                disabled={saving || !canManage}
                              />
                            </div>
                            {status && (
                              <span className="text-[11.5px] pl-11" style={{ color: t.mutedText }}>
                                Last activity: {formatRelativeTime(status.lastActivityAt)}
                                {status.countLast7Days > 0 && ` · ${status.countLast7Days} in the last 7 days`}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
            {saving && (
              <span className="text-[12px]" style={{ color: t.mutedText }}>Saving&hellip;</span>
            )}
            {saveError && (
              <span className="text-[12px]" style={{ color: t.errorText }}>{saveError}</span>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default React.memo(AutomationSettingsSection)
